import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown, AlertTriangle, Banknote } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'

interface CashFlow {
  days: number
  inflow_fils: number
  outflow_fils: number
  net_fils: number
  forecast_next_fils: number
  forecast_due_count: number
  order_count: number
  payment_count: number
  weekly: Array<{ week_start: string; inflow_fils: number; outflow_fils: number }>
}

const PRESETS = [7, 30, 60, 90, 180] as const

export default function CashFlow() {
  const [days, setDays] = useState<number>(30)
  const { data, isLoading } = useQuery<CashFlow>({
    queryKey: ['finance-cash-flow', days],
    queryFn: () => api.get(`/finance/cash-flow?days=${days}`).then(r => r.data),
  })

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Banknote size={20} className="text-[#c41e3a]" />
            Cash Flow
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Trailing inflows (order revenue) minus outflows (supplier payments), plus a forecast of what's due to suppliers over the next window.
          </p>
        </div>
        <div className="flex gap-1.5">
          {PRESETS.map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-md border ${days === d ? 'border-[#c41e3a] bg-[#c41e3a]/15 text-white' : 'border-[#3a3a3a] text-gray-400 hover:bg-white/5'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
      ) : data ? (
        <>
          {/* Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Tile
              icon={<TrendingUp size={18} />}
              label="Inflows"
              value={formatCurrency(data.inflow_fils)}
              sublabel={`${data.order_count} orders`}
              tone="green"
            />
            <Tile
              icon={<TrendingDown size={18} />}
              label="Outflows"
              value={formatCurrency(data.outflow_fils)}
              sublabel={`${data.payment_count} supplier payments`}
              tone="red"
            />
            <Tile
              icon={<Banknote size={18} />}
              label={`Net (last ${data.days}d)`}
              value={formatCurrency(data.net_fils)}
              tone={data.net_fils >= 0 ? 'green' : 'red'}
              bold
            />
            <Tile
              icon={<AlertTriangle size={18} />}
              label={`Due next ${data.days}d`}
              value={formatCurrency(data.forecast_next_fils)}
              sublabel={`${data.forecast_due_count} invoices`}
              tone="amber"
            />
          </div>

          {/* Weekly bar chart */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6">
            <div className="text-sm font-semibold text-white mb-4">Weekly inflows vs outflows</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.weekly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2e2e2e" />
                  <XAxis dataKey="week_start" stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}`} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                    formatter={(v, name) => [formatCurrency(Number(v)), name === 'inflow_fils' ? 'Inflows' : 'Outflows']}
                  />
                  <Legend formatter={(v) => v === 'inflow_fils' ? 'Inflows' : 'Outflows'} />
                  <Bar dataKey="inflow_fils" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outflow_fils" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {data.forecast_next_fils > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-200">
                <span className="font-semibold">{formatCurrency(data.forecast_next_fils)}</span> is due to suppliers over the next {data.days} days across {data.forecast_due_count} invoices.
                {data.net_fils > 0 && data.forecast_next_fils > data.net_fils && (
                  <> Current cash inflow pace covers <span className="font-semibold">{Math.round((data.net_fils / data.forecast_next_fils) * 100)}%</span> of that.</>
                )}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

function Tile({ icon, label, value, sublabel, tone, bold }: {
  icon: React.ReactNode
  label: string
  value: string
  sublabel?: string
  tone: 'green' | 'red' | 'amber'
  bold?: boolean
}) {
  const toneClass = tone === 'green' ? 'text-green-400' : tone === 'red' ? 'text-red-400' : 'text-amber-400'
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2">
        <div className={toneClass}>{icon}</div>
        <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      </div>
      <div className={`text-2xl font-mono font-bold mt-2 ${bold ? toneClass : 'text-white'}`}>{value}</div>
      {sublabel && <div className="text-[11px] text-gray-500 mt-1">{sublabel}</div>}
    </div>
  )
}
